import * as THREE from "three";
import { SoftBox } from "./roundedBoxes";
import type { AvatarConfig } from "@shared/types";
import { LANE_POSITIONS, RUNNER } from "@shared/constants";
import type { Path } from "./Path";
import type { MovementMode } from "../../local/gameSettings";
// Imported FBX character model + animation clips: scaffolding kept so we can
// revisit retargeting onto the procedural primitives later. Currently the
// procedural body + sine-wave run cycle drives all animation — see
// animateRunner() below — because Mixamo/Sketchfab clips animate bones by
// name and the procedural rig has no skeleton to receive them.
// import { cloneCharacterModel, tintCharacterModel, getRunClip } from "./characterModel";

type State = "run" | "jump" | "hit";

// Knockdown is a 3-phase sequence inside the "hit" state:
//   1. tumble  — ragdoll, no input
//   2. standup — get-up animation, no input
//   3. vulnerable — input returns but jump locked briefly
type HitPhase = "tumble" | "standup" | "vulnerable" | "none";

// Cut the post-hit freeze in half so a spinner / ball clip doesn't lock the
// player out of input long enough to feel punishing.
const TUMBLE_MS = 210;
const STANDUP_MS = 240;
const VULNERABLE_MS = 220;

const _tmpVec = new THREE.Vector3();
// No invisible boundary wall — sandbox mode means the only collision
// is from actual rendered meshes (raycast). The lane clamp is gated
// here at Infinity which is equivalent to "off"; flip via the
// SANDBOX_CONFIG.enableInvisibleBoundaryWalls flag if you ever want it
// back (you'd need to import the config from sandboxConfig).
const FREE_HALF_WIDTH = Number.POSITIVE_INFINITY;
const FREE_STEER_ACCEL = 34;
const FREE_STEER_DAMPING = 10;
const MAX_LATERAL_SPEED = 13;
const PLAYER_SCALE = 0.25;

function buildHat(id: string): THREE.Object3D {
  const g = new THREE.Group();
  switch (id) {
    case "cap-blue":
    case "cap-red":
    case "cap-green":
    case "cap-purple": {
      const colorMap: Record<string, number> = {
        "cap-blue": 0x2563eb,
        "cap-red": 0xdc2626,
        "cap-green": 0x16a34a,
        "cap-purple": 0x9b5cf6,
      };
      const color = colorMap[id]!;
      const crown = new THREE.Mesh(
        new SoftBox(0.78, 0.32, 0.78),
        new THREE.MeshStandardMaterial({ color }),
      );
      crown.position.set(0, 2.92, 0);
      crown.castShadow = true;
      g.add(crown);
      const brim = new THREE.Mesh(
        new SoftBox(0.78, 0.08, 0.45),
        new THREE.MeshStandardMaterial({ color }),
      );
      brim.position.set(0, 2.78, 0.5);
      brim.castShadow = true;
      g.add(brim);
      break;
    }
    case "beanie":
    case "beanie-blue": {
      const color = id === "beanie-blue" ? 0x2563eb : 0xef476f;
      const beanie = new THREE.Mesh(
        new SoftBox(0.82, 0.4, 0.82),
        new THREE.MeshStandardMaterial({ color }),
      );
      beanie.position.set(0, 2.96, 0);
      beanie.castShadow = true;
      g.add(beanie);
      const pom = new THREE.Mesh(
        new THREE.SphereGeometry(0.12, 8, 8),
        new THREE.MeshStandardMaterial({ color: 0xffffff }),
      );
      pom.position.set(0, 3.22, 0);
      pom.castShadow = true;
      g.add(pom);
      break;
    }
    case "top-hat": {
      const black = new THREE.MeshStandardMaterial({ color: 0x111827, roughness: 0.4 });
      const brim = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.62, 0.08, 24), black);
      brim.position.y = 2.78;
      brim.castShadow = true;
      g.add(brim);
      const stack = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.4, 0.7, 24), black);
      stack.position.y = 3.17;
      stack.castShadow = true;
      g.add(stack);
      const band = new THREE.Mesh(
        new THREE.CylinderGeometry(0.41, 0.41, 0.1, 24),
        new THREE.MeshStandardMaterial({ color: 0xff2ca4, roughness: 0.4 }),
      );
      band.position.y = 2.88;
      g.add(band);
      break;
    }
    case "wizard": {
      const purple = new THREE.MeshStandardMaterial({ color: 0x6d28d9, roughness: 0.5 });
      const cone = new THREE.Mesh(new THREE.ConeGeometry(0.5, 1.15, 16), purple);
      cone.position.y = 3.32;
      cone.castShadow = true;
      g.add(cone);
      const goldMat = new THREE.MeshStandardMaterial({ color: 0xffd166, emissive: 0xffb020, emissiveIntensity: 0.4 });
      for (let k = 0; k < 5; k++) {
        const star = new THREE.Mesh(new THREE.IcosahedronGeometry(0.07, 0), goldMat);
        const ang = (k / 5) * Math.PI * 2;
        const yT = 0.2 + (k % 2) * 0.35;
        const r = 0.46 - yT * 0.4;
        star.position.set(Math.sin(ang) * r, 2.85 + yT, Math.cos(ang) * r);
        g.add(star);
      }
      const brim = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.7, 0.08, 24), purple);
      brim.position.y = 2.78;
      brim.castShadow = true;
      g.add(brim);
      break;
    }
    case "horns": {
      const redMat = new THREE.MeshStandardMaterial({ color: 0xdc2626, roughness: 0.42, metalness: 0.05 });
      for (const x of [-0.22, 0.22]) {
        const horn = new THREE.Mesh(new THREE.ConeGeometry(0.13, 0.45, 14), redMat);
        horn.position.set(x, 2.92, -0.05);
        horn.rotation.z = x < 0 ? 0.3 : -0.3;
        horn.castShadow = true;
        g.add(horn);
      }
      break;
    }
    case "headband": {
      const band = new THREE.Mesh(
        new SoftBox(0.88, 0.16, 0.7),
        new THREE.MeshStandardMaterial({ color: 0xef4444 }),
      );
      band.position.y = 2.72;
      band.castShadow = true;
      g.add(band);
      const knot = new THREE.Mesh(
        new THREE.SphereGeometry(0.12, 14, 10),
        new THREE.MeshStandardMaterial({ color: 0xef4444 }),
      );
      knot.position.set(0, 2.75, -0.42);
      g.add(knot);
      break;
    }
    case "bow": {
      const pinkMat = new THREE.MeshStandardMaterial({ color: 0xff5fa7, roughness: 0.4 });
      for (const x of [-0.18, 0.18]) {
        const loop = new THREE.Mesh(new THREE.SphereGeometry(0.16, 16, 12), pinkMat);
        loop.scale.set(1, 0.7, 0.5);
        loop.position.set(x, 2.78, 0.18);
        loop.castShadow = true;
        g.add(loop);
      }
      const knot = new THREE.Mesh(new THREE.SphereGeometry(0.08, 12, 10), pinkMat);
      knot.position.set(0, 2.78, 0.18);
      g.add(knot);
      break;
    }
    case "crown": {
      const band = new THREE.Mesh(
        new SoftBox(0.78, 0.18, 0.78),
        new THREE.MeshStandardMaterial({ color: 0xffd166, metalness: 0.6, roughness: 0.3 }),
      );
      band.position.set(0, 2.85, 0);
      band.castShadow = true;
      g.add(band);
      for (let i = -1; i <= 1; i++) {
        for (let j = -1; j <= 1; j++) {
          if (Math.abs(i) + Math.abs(j) !== 1) continue;
          const spike = new THREE.Mesh(
            new THREE.ConeGeometry(0.1, 0.3, 4),
            new THREE.MeshStandardMaterial({ color: 0xffd166, metalness: 0.6, roughness: 0.3 }),
          );
          spike.position.set(i * 0.3, 3.05, j * 0.3);
          g.add(spike);
        }
      }
      break;
    }
    case "party-hat": {
      const cone = new THREE.Mesh(
        new THREE.ConeGeometry(0.42, 0.95, 16),
        new THREE.MeshStandardMaterial({ color: 0xa855f7, roughness: 0.45 }),
      );
      cone.position.set(0, 3.18, 0);
      cone.castShadow = true;
      g.add(cone);
      // Polka dots on the cone
      for (let k = 0; k < 8; k++) {
        const dot = new THREE.Mesh(
          new THREE.SphereGeometry(0.06, 8, 6),
          new THREE.MeshStandardMaterial({ color: 0xffd166 }),
        );
        const ang = (k / 8) * Math.PI * 2;
        const yT = 0.2 + (k % 3) * 0.22;
        const r = 0.42 - yT * 0.42;
        dot.position.set(Math.sin(ang) * r, 2.8 + yT, Math.cos(ang) * r);
        g.add(dot);
      }
      const tip = new THREE.Mesh(
        new THREE.SphereGeometry(0.13, 10, 8),
        new THREE.MeshStandardMaterial({ color: 0xffd166 }),
      );
      tip.position.set(0, 3.7, 0);
      g.add(tip);
      break;
    }
    case "halo": {
      const halo = new THREE.Mesh(
        new THREE.TorusGeometry(0.42, 0.08, 12, 28),
        new THREE.MeshStandardMaterial({
          color: 0xfef08a,
          emissive: 0xfff3a8,
          emissiveIntensity: 0.45,
          metalness: 0.3,
          roughness: 0.25,
        }),
      );
      halo.rotation.x = Math.PI / 2;
      halo.position.set(0, 3.18, 0);
      g.add(halo);
      break;
    }
  }
  return g;
}

/**
 * Procedural shirt material — bakes the chosen pattern into a CanvasTexture so
 * the torso sphere can wear stripes/dots/star without UV editing.
 */
function makeShirtMaterial(base: THREE.Color, pattern: string): THREE.MeshStandardMaterial {
  if (pattern === "solid") {
    return new THREE.MeshStandardMaterial({ color: base, roughness: 0.55, metalness: 0.03 });
  }
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const baseHex = "#" + base.getHexString();
  ctx.fillStyle = baseHex;
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = "#ffffff";
  if (pattern === "stripes") {
    const stripeH = size / 8;
    for (let i = 0; i < 8; i += 2) ctx.fillRect(0, i * stripeH, size, stripeH);
  } else if (pattern === "dots") {
    for (let y = 24; y < size; y += 48) {
      for (let x = 24; x < size; x += 48) {
        ctx.beginPath();
        ctx.arc(x + (Math.floor(y / 48) % 2 === 0 ? 0 : 24), y, 10, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  } else if (pattern === "star") {
    ctx.fillStyle = "#ffd166";
    const drawStar = (cx: number, cy: number, r: number) => {
      ctx.beginPath();
      for (let i = 0; i < 10; i++) {
        const ang = (i / 10) * Math.PI * 2 - Math.PI / 2;
        const rad = i % 2 === 0 ? r : r * 0.45;
        const x = cx + Math.cos(ang) * rad;
        const y = cy + Math.sin(ang) * rad;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();
    };
    for (let y = 40; y < size; y += 80) {
      for (let x = 40; x < size; x += 80) {
        drawStar(x + (Math.floor(y / 80) % 2 === 0 ? 0 : 40), y, 22);
      }
    }
  } else if (pattern === "checker") {
    const sq = size / 8;
    for (let r = 0; r < 8; r++) {
      for (let c = 0; c < 8; c++) {
        if ((r + c) % 2 === 0) ctx.fillRect(c * sq, r * sq, sq, sq);
      }
    }
  } else if (pattern === "zigzag") {
    const rowH = size / 8;
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 6;
    for (let y = 0; y < size; y += rowH) {
      ctx.beginPath();
      const flip = ((y / rowH) | 0) % 2 === 0;
      ctx.moveTo(0, y + (flip ? rowH * 0.25 : rowH * 0.75));
      for (let x = 0; x < size; x += 32) {
        const yOff = ((x / 32) % 2 === 0) === flip ? rowH * 0.25 : rowH * 0.75;
        ctx.lineTo(x + 16, y + yOff);
      }
      ctx.stroke();
    }
  } else if (pattern === "hearts") {
    ctx.fillStyle = "#ffffff";
    const drawHeart = (cx: number, cy: number, r: number) => {
      ctx.beginPath();
      ctx.moveTo(cx, cy + r * 0.4);
      ctx.bezierCurveTo(cx - r, cy - r * 0.4, cx - r * 1.1, cy + r * 0.6, cx, cy + r);
      ctx.bezierCurveTo(cx + r * 1.1, cy + r * 0.6, cx + r, cy - r * 0.4, cx, cy + r * 0.4);
      ctx.fill();
    };
    for (let y = 32; y < size; y += 56) {
      for (let x = 32; x < size; x += 56) {
        drawHeart(x + (Math.floor(y / 56) % 2 === 0 ? 0 : 28), y, 14);
      }
    }
  } else if (pattern === "lightning") {
    ctx.fillStyle = "#fde047";
    const drawBolt = (cx: number, cy: number, s: number) => {
      ctx.beginPath();
      ctx.moveTo(cx + s * 0.4, cy - s);
      ctx.lineTo(cx - s * 0.2, cy);
      ctx.lineTo(cx + s * 0.1, cy);
      ctx.lineTo(cx - s * 0.4, cy + s);
      ctx.lineTo(cx + s * 0.3, cy + s * 0.1);
      ctx.lineTo(cx, cy + s * 0.1);
      ctx.closePath();
      ctx.fill();
    };
    for (let y = 40; y < size; y += 80) {
      for (let x = 40; x < size; x += 80) {
        drawBolt(x + (Math.floor(y / 80) % 2 === 0 ? 0 : 40), y, 18);
      }
    }
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return new THREE.MeshStandardMaterial({ map: tex, roughness: 0.55, metalness: 0.03 });
}

function angleDelta(current: number, target: number) {
  return THREE.MathUtils.euclideanModulo(target - current + Math.PI, Math.PI * 2) - Math.PI;
}

function dampAngle(current: number, target: number, alpha: number) {
  return current + angleDelta(current, target) * THREE.MathUtils.clamp(alpha, 0, 1);
}

function smoothstep(t: number) {
  t = THREE.MathUtils.clamp(t, 0, 1);
  return t * t * (3 - 2 * t);
}

function powerPulse(now: number, start: number, end: number) {
  if (now >= end) return 0;
  const t = (now - start) / Math.max(1, end - start);
  return Math.sin(THREE.MathUtils.clamp(t, 0, 1) * Math.PI);
}

function capsulePart(
  radius: number,
  length: number,
  color: THREE.ColorRepresentation,
  roughness = 0.58,
) {
  const mesh = new THREE.Mesh(
    new THREE.CapsuleGeometry(radius, length, 16, 24),
    new THREE.MeshStandardMaterial({ color, roughness, metalness: 0.03 }),
  );
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

export class Player {
  group = new THREE.Group();
  state: State = "run";
  lane = 2;
  laneTarget = 2;
  vy = 0;
  y = 0;
  jumpsLeft = 1;
  doubleJumpUnlocked = false;
  hitUntil = 0;
  hitPhase: HitPhase = "none";
  /** Time when the current hit phase transitions to the next. */
  private hitPhaseUntil = 0;
  /** Time when jump becomes available again after standing up. */
  private jumpLockUntil = 0;

  private parts: { ll: THREE.Object3D; rl: THREE.Object3D };
  private body: THREE.Group;
  private runT = 0;
  /** Smooth lateral lane offset (m), interpolated toward LANE_POSITIONS[laneTarget]. */
  laneOffset = 0;
  /** Lane-shift critically-damped spring velocity. */
  private laneVel = 0;
  /** Optional path the player runs along; if set, group position/rotation come from it. */
  path: Path | null = null;
  /** Arc length progress along the path. */
  s = 0;
  /** Constant lateral offset for MP slot lineups (added to laneOffset). */
  slotOffset = 0;
  movementMode: MovementMode = "free";
  /** Optional per-level gravity override (m/s²). Set by Game from
   *  GameConfig.gravityOverride. Falls back to RUNNER.gravity when null. */
  gravityOverride: number | null = null;
  // Crumble (death) state.
  private crumbling = false;
  private crumblePieces: {
    mesh: THREE.Object3D;
    vel: THREE.Vector3;
    spin: THREE.Vector3;
    /** Original parent + local transform — used by revive() to put the
     *  body back together after a survival respawn. */
    origParent: THREE.Object3D;
    origPos: THREE.Vector3;
    origQuat: THREE.Quaternion;
    origScale: THREE.Vector3;
  }[] = [];
  private boostUntil = 0;
  private boostStartedAt = 0;
  private tumbleUntil = 0;
  private tumbleSpin = 0;
  private moveIntensity = 0;
  private lastJumpAt = 0;
  private fallPose = false;
  private groundedOnSurface = false;
  private desiredWorldYaw: number | null = null;
  private desiredMoveAmount = 0;
  private facingAlignment = 1;

  constructor(avatar: AvatarConfig) {
    const suit = new THREE.Color(avatar.shirtColor ?? "#ff2ca4");
    const faceColor = new THREE.Color(avatar.skinColor ?? "#ffd6ef").lerp(new THREE.Color("#ffffff"), 0.34);
    const footColor = new THREE.Color(avatar.pantsColor ?? avatar.shirtColor ?? "#e11d8a")
      .lerp(new THREE.Color("#111827"), 0.12);

    this.body = new THREE.Group();
    this.group.add(this.body);
    this.body.scale.setScalar(PLAYER_SCALE);

    // Procedural primitive body. The FBX path was removed — Mixamo/Sketchfab
    // animation clips drive bones by name and the primitives have no
    // skeleton to receive those tracks. The sine-wave animation in
    // animateRunner() below provides the run/jump cycles instead.
    let ll: THREE.Group;
    let rl: THREE.Group;
    {
      const torso = new THREE.Mesh(
        new THREE.SphereGeometry(0.92, 32, 32),
        makeShirtMaterial(suit, avatar.patternId ?? "solid"),
      );
      torso.scale.set(0.9, 1.34, 0.68);
      torso.position.y = 1.62;
      torso.castShadow = true;
      torso.receiveShadow = true;
      this.body.add(torso);

      const face = new THREE.Mesh(
        new THREE.SphereGeometry(0.38, 24, 20),
        new THREE.MeshStandardMaterial({ color: faceColor, roughness: 0.5 }),
      );
      face.scale.set(1.05, 1.18, 0.15);
      face.position.set(0, 2.05, 0.63);
      this.body.add(face);

      const eyeMat = new THREE.MeshBasicMaterial({ color: 0x111111 });
      for (const dx of [-0.15, 0.15]) {
        const eye = new THREE.Mesh(new THREE.CapsuleGeometry(0.035, 0.12, 6, 12), eyeMat);
        eye.position.set(dx, 2.07, 0.7);
        this.body.add(eye);
      }

      const hatId = avatar.hatId ?? "none";
      if (hatId !== "none") {
        const hat = buildHat(hatId);
        this.body.add(hat);
      }

      const leg = (x: number) => {
        const pivot = new THREE.Group();
        pivot.position.set(x, 0.58, 0.1);
        const m = capsulePart(0.18, 0.5, suit);
        m.position.y = -0.3;
        const foot = new THREE.Mesh(
          new THREE.SphereGeometry(0.22, 18, 14),
          new THREE.MeshStandardMaterial({ color: footColor, roughness: 0.62, metalness: 0.02 }),
        );
        foot.scale.set(0.95, 0.62, 1.35);
        foot.position.set(0, -0.64, 0.15);
        foot.castShadow = true;
        pivot.add(m);
        pivot.add(foot);
        this.body.add(pivot);
        return pivot;
      };
      ll = leg(-0.28);
      rl = leg(0.28);
    }

    this.parts = { ll, rl };

    this.group.position.x = LANE_POSITIONS[this.lane]!;
    this.group.position.z = 0;
    this.laneOffset = LANE_POSITIONS[this.lane]!;
  }

  moveLane(delta: number) {
    if (this.isInputLocked()) return;
    if (this.movementMode === "free") {
      this.laneVel += delta * 4.5;
      return;
    }
    this.laneTarget = Math.max(0, Math.min(LANE_POSITIONS.length - 1, this.laneTarget + delta));
  }

  /** Dodge: skip TWO lanes in the given direction (double-tap). */
  dodge(delta: number) {
    if (this.isInputLocked()) return;
    if (this.movementMode === "free") {
      this.laneVel += delta * 9;
      return;
    }
    this.laneTarget = Math.max(
      0,
      Math.min(LANE_POSITIONS.length - 1, this.laneTarget + delta * 2),
    );
  }

  steer(axis: number, dt: number) {
    if (this.movementMode !== "free" || this.isInputLocked()) return;
    const targetVel = THREE.MathUtils.clamp(axis, -1, 1) * 11.5;
    // Snappier steering (was 12/s). Slightly more responsive air control.
    const airborne = this.state === "jump" || this.y > 0.08;
    const followRate = airborne ? 14 : 18;
    const follow = 1 - Math.exp(-followRate * dt);
    this.laneVel += (targetVel - this.laneVel) * follow;
  }

  bumpSideways(direction: number) {
    if (this.movementMode !== "free" || this.isInputLocked()) return;
    this.applyImpulse(Math.max(-1, Math.min(1, direction)) * 7, 0);
  }

  applyImpulse(lateral: number, vertical = 0) {
    if (this.movementMode === "free") {
      this.laneVel = THREE.MathUtils.clamp(this.laneVel + lateral, -MAX_LATERAL_SPEED, MAX_LATERAL_SPEED);
    } else if (Math.abs(lateral) > 0.1) {
      this.moveLane(lateral > 0 ? 1 : -1);
    }
    if (vertical > 0) {
      this.vy = Math.max(this.vy, vertical);
      this.y = Math.max(0.05, this.y);
      this.state = "jump";
    }
  }

  setMoveIntensity(value: number) {
    this.moveIntensity = THREE.MathUtils.clamp(value, 0, 1);
  }

  setDesiredFacing(yaw: number | null, moveAmount = 0) {
    this.desiredWorldYaw = yaw;
    this.desiredMoveAmount = THREE.MathUtils.clamp(moveAmount, 0, 1);
    if (yaw == null || this.desiredMoveAmount < 0.05) {
      this.facingAlignment = 1;
      return;
    }
    const angle = Math.abs(angleDelta(this.group.rotation.y, yaw));
    const rawAlignment = THREE.MathUtils.clamp(1 - angle / 1.35, 0, 1);
    this.facingAlignment = smoothstep(rawAlignment);
  }

  getMovementAlignment() {
    return this.facingAlignment;
  }

  getLateralVelocity() {
    return this.laneVel;
  }

  getRadius() {
    return 0.34;
  }

  resetOnCourse(path: Path, s: number, laneOffset = 0) {
    // If the player crumbled (e.g. survival respawn), reassemble before
    // placing them back on the course.
    if (this.crumbling) this.revive();
    this.path = path;
    this.s = s;
    this.laneOffset = laneOffset;
    this.laneTarget = 2;
    this.lane = 2;
    this.laneVel = 0;
    this.y = 0;
    this.vy = 0;
    this.jumpsLeft = 1;
    this.groundedOnSurface = false;
    this.state = "run";
    this.fallPose = false;
    this.group.rotation.x = 0;
    this.group.rotation.z = 0;
    this.body.visible = true;
  }

  setFallPose(enabled: boolean) {
    this.fallPose = enabled;
    if (enabled) {
      this.state = "hit";
      this.hitUntil = Number.POSITIVE_INFINITY;
      this.tumbleUntil = Number.POSITIVE_INFINITY;
      this.tumbleSpin = this.laneVel >= 0 ? -1 : 1;
    } else if (this.state === "hit" && !Number.isFinite(this.hitUntil)) {
      this.state = "run";
      this.hitUntil = 0;
      this.tumbleUntil = 0;
    }
  }

  /** -1 or +1: the lateral direction the player was knocked toward.
   *  Used by counter-struggle: pushing the joystick the opposite way
   *  recovers from the knockdown faster (Fall Guys-style). */
  private knockbackDir = 0;
  /** Latest joystick lateral input, sampled even while input is locked so the
   *  player can "struggle" against the knockdown by pushing opposite. */
  struggleInput = 0;

  knockdown(now: number, lateral: number, vertical = 0.45) {
    this.applyImpulse(lateral, vertical);
    this.state = "hit";
    this.hitPhase = "tumble";
    this.hitPhaseUntil = now + TUMBLE_MS;
    // hitUntil is the total recovery point (tumble + standup); during the
    // final "vulnerable" sub-phase the player can move again.
    const total = TUMBLE_MS + STANDUP_MS;
    this.hitUntil = now + total;
    this.tumbleUntil = now + TUMBLE_MS;
    this.tumbleSpin = lateral >= 0 ? -1 : 1;
    this.knockbackDir = lateral >= 0 ? 1 : -1;
  }

  /** True while the player can't accept movement/action input. */
  isInputLocked(): boolean {
    if (this.state === "hit" && (this.hitPhase === "tumble" || this.hitPhase === "standup")) return true;
    return false;
  }

  /** Forward boost (m/s) to add to base speed. Kept for API compatibility
   *  with Game.ts; always 0 now that dive is removed. */
  getForwardBoost(): number {
    return 0;
  }

  overlapsLane(lane: number, radius = 0.75): boolean {
    return Math.abs(this.laneOffset - LANE_POSITIONS[lane]!) <= radius;
  }

  /** Coyote-time grace: window (ms) after losing ground-contact during which
   *  a fresh jump is still allowed. Forgives slightly-late jump presses near
   *  ledges. */
  private static COYOTE_MS = 140;
  /** Jump-buffer grace: window (ms) before landing during which an early
   *  jump press is queued and auto-fired the instant the player touches
   *  ground. Forgives slightly-early jump presses. */
  private static JUMP_BUFFER_MS = 160;
  private lastGroundedAt: number = 0;
  private bufferedJumpAt: number = -1e9;

  /** Monotonic counter incremented on every successful jump press (ground
   *  or air). Read by the network broadcast so remote ghosts can replay
   *  double-jumps. `state` alone doesn't change between the first jump
   *  and the air-jump, so we need a separate signal. */
  jumpSeq = 0;

  jump(now: number) {
    if (this.isInputLocked()) return;
    if (now < this.jumpLockUntil) return;
    const inAir = this.y > 0.08 && !this.groundedOnSurface;
    // Coyote-time: even if we're technically airborne, allow the jump if it
    // happened within COYOTE_MS of last leaving the ground AND we still have
    // a jump charge. Keeps timing forgiving near ledges.
    const withinCoyote = inAir && (now - this.lastGroundedAt) < Player.COYOTE_MS;
    if (inAir && !withinCoyote) {
      // Genuinely airborne — buffer the press; if we land within the
      // buffer window the landOn() call will fire the jump for us.
      this.bufferedJumpAt = now;
      return;
    }
    if (this.jumpsLeft <= 0) return;
    this.vy = RUNNER.jumpVelocity * 0.78;
    this.jumpsLeft -= 1;
    this.state = "jump";
    this.groundedOnSurface = false;
    this.lastJumpAt = now;
    this.jumpSeq++;
    // Clear the buffer once a jump actually fires.
    this.bufferedJumpAt = -1e9;
  }

  landOn(relativeHeight: number) {
    // Was hard-clamped at -0.05; relaxed to -10 so the space-ship rescue
    // path (~2m below heightAt) can land the player. Normal levels never
    // produce negative relative heights so this is effectively a no-op
    // for them.
    if (relativeHeight < -10) return;
    this.y = relativeHeight;
    this.vy = 0;
    this.jumpsLeft = 1;
    this.groundedOnSurface = true;
    this.lastGroundedAt = performance.now();
    // Jump-buffer: if the player pressed jump just before landing, fire it
    // now. This is the "rubber-banding" forgiveness on the landing side.
    if (performance.now() - this.bufferedJumpAt < Player.JUMP_BUFFER_MS) {
      const now = performance.now();
      this.bufferedJumpAt = -1e9;
      // Immediate jump using the same code path. We don't go through jump()
      // because we want to bypass the airborne check (we're definitely on
      // the ground this very frame).
      this.vy = RUNNER.jumpVelocity * 0.78;
      this.jumpsLeft = Math.max(0, this.jumpsLeft - 1);
      this.state = "jump";
      this.groundedOnSurface = false;
      this.lastJumpAt = now;
      return;
    }
    if (this.state === "jump") this.state = "run";
  }

  clearSurfaceSupport() {
    // Stamp the "last grounded" timestamp the moment we leave a surface so
    // the coyote-time window starts here, not on the next user input.
    if (this.groundedOnSurface) this.lastGroundedAt = performance.now();
    this.groundedOnSurface = false;
  }

  /** True while the player is being held by a standable surface (moving
   *  platform, raised ground, etc.). Used by Game to disambiguate
   *  "elevated but supported" from "in mid-air". */
  isGroundedOnSurface(): boolean {
    return this.groundedOnSurface;
  }

  hit(now: number) {
    this.knockdown(now, this.laneVel >= 0 ? -5 : 5, 3.5);
  }

  boost(now: number) {
    this.boostStartedAt = now;
    this.boostUntil = now + 1000;
  }

  /** Detach all body parts and give them random velocity/spin. */
  crumble() {
    if (this.crumbling) return;
    this.crumbling = true;
    const collect: THREE.Object3D[] = [];
    this.body.traverse((o) => {
      if (o instanceof THREE.Mesh) collect.push(o);
    });
    for (const m of collect) {
      // Snapshot original parent + local transform BEFORE detaching so we
      // can rebuild the body on revive().
      const origParent = m.parent ?? this.body;
      const origPos = m.position.clone();
      const origQuat = m.quaternion.clone();
      const origScale = m.scale.clone();
      // Re-parent to player group (not body) so we can drive each piece freely.
      const worldPos = new THREE.Vector3();
      m.getWorldPosition(worldPos);
      this.group.attach(m);
      m.position.copy(this.group.worldToLocal(worldPos));
      const v = new THREE.Vector3(
        (Math.random() - 0.5) * 4,
        2 + Math.random() * 4,
        (Math.random() - 0.5) * 2,
      );
      const spin = new THREE.Vector3(
        (Math.random() - 0.5) * 8,
        (Math.random() - 0.5) * 8,
        (Math.random() - 0.5) * 8,
      );
      this.crumblePieces.push({ mesh: m, vel: v, spin, origParent, origPos, origQuat, origScale });
    }
    this.body.visible = false;
  }

  /** Undo crumble() — re-attach all flying pieces to their original
   *  parent + reset transforms, then re-show the body. Used by survival
   *  respawn so the player is whole again after a correct answer. */
  revive() {
    if (!this.crumbling) return;
    for (const p of this.crumblePieces) {
      p.origParent.add(p.mesh);
      p.mesh.position.copy(p.origPos);
      p.mesh.quaternion.copy(p.origQuat);
      p.mesh.scale.copy(p.origScale);
    }
    this.crumblePieces = [];
    this.crumbling = false;
    this.body.visible = true;
  }

  /** AABB used for collision against obstacles. */
  bounds(): THREE.Box3 {
    const x = this.group.position.x;
    const z = this.group.position.z;
    const y = this.y;
    const h = 2.8;
    const yOff = 1.4;
    return new THREE.Box3(
      new THREE.Vector3(x - 0.45, y + yOff - h / 2, z - 0.4),
      new THREE.Vector3(x + 0.45, y + yOff + h / 2, z + 0.4),
    );
  }

  update(dt: number, now: number) {
    if (this.crumbling) {
      // Free-flying voxel pieces with gravity.
      for (const p of this.crumblePieces) {
        p.vel.y -= 12 * dt;
        p.mesh.position.addScaledVector(p.vel, dt);
        p.mesh.rotation.x += p.spin.x * dt;
        p.mesh.rotation.y += p.spin.y * dt;
        p.mesh.rotation.z += p.spin.z * dt;
        // Bounce off ground (y=0 in player-local space below player center)
        if (p.mesh.position.y < -1.4) {
          p.mesh.position.y = -1.4;
          p.vel.y *= -0.35;
          p.vel.x *= 0.85;
          p.vel.z *= 0.85;
        }
      }
      return;
    }
    if (this.movementMode === "free") {
      const airborne = this.state === "jump" || this.y > 0.08;
      // Strong air control so the player can save bad jumps; tight damping
      // on the ground.
      const dampingRate =
        this.state === "hit" ? 2.4 :
        airborne ? 1.4 :
        FREE_STEER_DAMPING;
      const damping = Math.exp(-dampingRate * dt);
      this.laneVel *= damping;
      this.laneVel = THREE.MathUtils.clamp(this.laneVel, -MAX_LATERAL_SPEED, MAX_LATERAL_SPEED);
      this.laneOffset += this.laneVel * dt;
      this.laneOffset = THREE.MathUtils.clamp(this.laneOffset, -FREE_HALF_WIDTH, FREE_HALF_WIDTH);
      let nearest = 0;
      let best = Infinity;
      for (let i = 0; i < LANE_POSITIONS.length; i++) {
        const d = Math.abs(this.laneOffset - LANE_POSITIONS[i]!);
        if (d < best) {
          best = d;
          nearest = i;
        }
      }
      this.laneTarget = nearest;
    } else {
      // Lane shift — critically-damped spring on the lane offset (interpreted in
      // the local "across-the-path" axis when running along a curved Path).
      const targetOffset = LANE_POSITIONS[this.laneTarget]!;
      const k = 60;
      const d = 2 * Math.sqrt(k);
      const ax = -k * (this.laneOffset - targetOffset) - d * this.laneVel;
      this.laneVel += ax * dt;
      this.laneOffset += this.laneVel * dt;
    }
    // If we're not on a path, write straight to world x. Otherwise the Game
    // (or path follower below) places us along the curve every frame.
    if (!this.path) this.group.position.x = this.laneOffset;
    else {
      const pos = this.path.positionAt(this.s, this.laneOffset + this.slotOffset, _tmpVec);
      this.group.position.x = pos.x;
      this.group.position.y = pos.y;
      this.group.position.z = pos.z;
      const targetYaw = this.desiredWorldYaw ?? this.group.rotation.y;
      const turnRate = this.desiredWorldYaw == null ? 9 : 11.5;
      this.group.rotation.y = dampAngle(this.group.rotation.y, targetYaw, 1 - Math.exp(-turnRate * dt));
      const angle = Math.abs(angleDelta(this.group.rotation.y, targetYaw));
      const rawAlignment = this.desiredWorldYaw == null ? 1 : THREE.MathUtils.clamp(1 - angle / 1.35, 0, 1);
      this.facingAlignment = this.desiredMoveAmount < 0.05 ? 1 : smoothstep(rawAlignment);
    }

    // Hit phase machine: tumble → standup → vulnerable → run.
    // Counter-struggle: while in tumble/standup, pushing the joystick in the
    // direction OPPOSITE to the knockback shortens the timer — the player
    // "fights" the slide. Magnitude scales how fast the recovery advances.
    if (this.state === "hit" && (this.hitPhase === "tumble" || this.hitPhase === "standup")) {
      const struggle = -this.struggleInput * this.knockbackDir; // [-1..1], +1 = opposing
      if (struggle > 0.25) {
        // At full opposing input, recovery is ~3x faster. We do this by
        // pulling the phase deadline earlier each frame.
        const advance = struggle * 1.7 * dt * 1000; // ms shaved off this frame
        this.hitPhaseUntil -= advance;
        this.hitUntil -= advance;
        this.tumbleUntil -= advance;
        // Also slow the actual slide momentum so they don't keep sliding.
        this.laneVel *= Math.exp(-struggle * 4.5 * dt);
      }
    }
    if (this.state === "hit") {
      if (this.hitPhase === "tumble" && now >= this.hitPhaseUntil) {
        this.hitPhase = "standup";
        this.hitPhaseUntil = now + STANDUP_MS;
      } else if (this.hitPhase === "standup" && now >= this.hitPhaseUntil) {
        this.hitPhase = "vulnerable";
        this.hitPhaseUntil = now + VULNERABLE_MS;
        this.jumpLockUntil = now + VULNERABLE_MS;
      } else if (this.hitPhase === "vulnerable" && now >= this.hitPhaseUntil) {
        this.hitPhase = "none";
        this.state = "run";
      }
    }

    // Vertical physics — sandbox mode: no built-in ground catch at
    // y=0. The OLD code snapped y to zero whenever it dropped below,
    // which acted as an invisible floor under every level (walk off
    // the rendered platform → silently stand on the path plane). The
    // only ground catch now is the raycast in Game.resolveStandableSurface
    // landing on an actual mesh; otherwise gravity keeps the player
    // falling forever.
    if (this.state === "jump" || this.y !== 0 || this.vy !== 0) {
      const g = this.gravityOverride ?? RUNNER.gravity;
      this.vy -= g * dt;
      this.y += this.vy * dt;
    }

    // Animation
    const boosting = now < this.boostUntil;
    const boostPower = powerPulse(now, this.boostStartedAt, this.boostUntil);
    const moveIntensity = this.state === "run" ? this.moveIntensity : 1;
    this.runT += dt * (10 + boostPower * 6) * Math.max(0.08, moveIntensity);
    if (this.state === "run") {
      const step = this.runT;
      const sway = Math.sin(step);
      const sideStep = Math.sin(step * 2);
      const footLiftL = Math.max(0, Math.sin(step));
      const footLiftR = Math.max(0, -Math.sin(step));
      const plantL = Math.max(0, -Math.sin(step));
      const plantR = Math.max(0, Math.sin(step));
      const legSwing = sway * (0.42 + boostPower * 0.18) * moveIntensity;

      // Correct-answer power-up: grounded lean and quicker feet, without a jump.
      this.parts.ll.rotation.x = -legSwing - footLiftL * 0.18;
      this.parts.rl.rotation.x = legSwing - footLiftR * 0.18;
      this.parts.ll.rotation.z = -0.08 - footLiftL * 0.16 * moveIntensity;
      this.parts.rl.rotation.z = 0.08 + footLiftR * 0.16 * moveIntensity;
      this.parts.ll.position.y = 0.58 + footLiftL * 0.18 * moveIntensity - plantL * 0.035 * moveIntensity;
      this.parts.rl.position.y = 0.58 + footLiftR * 0.18 * moveIntensity - plantR * 0.035 * moveIntensity;
      this.parts.ll.position.z = 0.1 + footLiftL * 0.1 * moveIntensity;
      this.parts.rl.position.z = 0.1 + footLiftR * 0.1 * moveIntensity;
      this.body.rotation.x = -0.16 * boostPower + Math.abs(sway) * 0.035 * moveIntensity;
      const turn = THREE.MathUtils.clamp(-this.laneVel * 0.035, -0.58, 0.58);
      this.body.rotation.y = turn + sideStep * 0.055 * moveIntensity;
      this.body.rotation.z = sway * (0.14 - boostPower * 0.05) * moveIntensity;
      const beanSquash = Math.abs(Math.sin(this.runT * 2)) * 0.025 * moveIntensity;
      const breath = Math.sin(now * 0.0032) * 0.018 * (1 - Math.min(1, moveIntensity * 0.85));
      const powerSquash = boostPower * 0.04;
      this.body.scale.set(
        PLAYER_SCALE * (1 + beanSquash * 0.45 - breath * 0.35 + powerSquash),
        PLAYER_SCALE * (1 - beanSquash + breath - powerSquash * 0.65),
        PLAYER_SCALE * (1 + beanSquash * 0.45 - breath * 0.35 + powerSquash),
      );
    } else if (this.state === "jump") {
      // Tiny feet tuck back/up for a simple bean jump.
      this.parts.ll.rotation.x = -1.2;
      this.parts.rl.rotation.x = -1.2;
      this.parts.ll.rotation.z = -0.05;
      this.parts.rl.rotation.z = 0.05;
      this.parts.ll.position.set(-0.28, 0.68, 0.16);
      this.parts.rl.position.set(0.28, 0.68, 0.16);
      this.body.rotation.x = -0.15;
      this.body.rotation.y = THREE.MathUtils.clamp(-this.laneVel * 0.025, -0.35, 0.35);
      this.body.scale.set(PLAYER_SCALE * 0.96, PLAYER_SCALE * 1.08, PLAYER_SCALE * 0.96);
    } else if (this.state === "hit") {
      // Ragdoll feel — bigger rotation arc, free-spinning legs, more
      // squash. The tumble axis is `tumbleSpin` (sign matches hit
      // direction), with a secondary head-over-heels rotation on X
      // during the tumble phase. Standup eases everything back to 0.
      const remaining = Math.max(0, Math.min(1, (this.tumbleUntil - now) / 980));
      const recover = 1 - remaining;
      const wobble = Math.sin(recover * Math.PI * 5) * (1 - recover);
      const flopT = Math.sin(remaining * Math.PI); // 0 → 1 → 0 over tumble
      // Legs flop loose during the tumble — long arcs that read as
      // limp limbs rather than the stiff "stop-mid-stride" pose.
      this.parts.ll.rotation.x = -1.1 - Math.sin(now * 0.013) * 0.4 * remaining + wobble * 0.25;
      this.parts.rl.rotation.x = 1.15 + Math.cos(now * 0.012) * 0.4 * remaining - wobble * 0.2;
      this.parts.ll.rotation.z = -0.32 - flopT * 0.3 * this.tumbleSpin;
      this.parts.rl.rotation.z =  0.32 + flopT * 0.3 * this.tumbleSpin;
      this.parts.ll.position.set(-0.28, 0.58, 0.1);
      this.parts.rl.position.set(0.28, 0.58, 0.1);
      // Body tumbles head-over-heels on X (forward flop) plus rolls
      // on Z in the direction of the hit. Both eased to 0 on standup.
      this.body.rotation.x = -1.6 * remaining + wobble * 0.22;
      this.body.rotation.y = this.tumbleSpin * 0.35 * remaining;
      this.body.rotation.z = this.tumbleSpin * (1.45 * remaining + wobble * 0.4);
      const squash = Math.sin(recover * Math.PI) * 0.28;
      this.body.scale.set(
        PLAYER_SCALE * (1 + squash * 0.8),
        PLAYER_SCALE * (1 - squash * 1.1),
        PLAYER_SCALE * (1 + squash * 0.5),
      );
      if (this.fallPose) {
        const spin = now * 0.006;
        this.parts.ll.rotation.x = -1.2 + Math.cos(spin * 0.9) * 0.35;
        this.parts.rl.rotation.x = 1.15 + Math.sin(spin * 1.2) * 0.35;
        this.body.rotation.x += dt * 3.2;
        this.body.rotation.z += this.tumbleSpin * dt * 4.4;
        this.body.scale.setScalar(PLAYER_SCALE);
      }
    }

    // Slide drops the body slightly so it reads as "low to the ground" but
    // never below it. Jump height adds on top.
    this.body.position.x = 0;
    this.body.position.y = 0;
    if (this.state === "run") {
      this.body.position.x = Math.sin(this.runT) * (0.035 - boostPower * 0.015) * moveIntensity;
      this.body.position.y += Math.abs(Math.sin(this.runT * 2)) * (0.11 - boostPower * 0.065) * moveIntensity;
      this.body.position.y += Math.sin(now * 0.0032) * 0.018 * (1 - Math.min(1, moveIntensity));
    }
    this.body.position.y += this.y;

    // Lateral lean — driven by current lane velocity for a natural body bank.
    const leanLerp = 1 - Math.exp(-10 * dt);
    if (this.state === "run" || this.state === "jump") {
      const lean = Math.max(-0.55, Math.min(0.55, -this.laneVel * 0.07));
      this.body.rotation.z += (lean - this.body.rotation.z) * leanLerp;
    } else if (this.state !== "hit") {
      this.body.rotation.z += (0 - this.body.rotation.z) * leanLerp;
    }

    this.body.visible = true;
  }
}
